import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Fragment, StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'motion/react';
import { ApiError } from './api/client.ts';
import { App } from './App.tsx';
import { ToastProvider } from './components/ui/index.tsx';
import { initI18n, t, useLocale } from './i18n/index.ts';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // No reintenta errores de cliente (permisos, no encontrado, validación).
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

// Tema guardado antes de pintar (evita parpadeo).
try {
  const t = localStorage.getItem('theme');
  if (t === 'light' || t === 'dark') document.documentElement.setAttribute('data-theme', t);
} catch {
  /* almacenamiento no disponible */
}

/** Al cambiar de idioma se vuelve a montar la interfaz (la caché de datos se conserva) para que todo texto se traduzca. */
function LocaleRoot({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  useEffect(() => {
    document.title = t('Gallarday · Árboles familiares');
  }, [locale]);
  return <Fragment key={locale}>{children}</Fragment>;
}

// Idioma antes de pintar (evita ver la interfaz en español un instante).
void initI18n().finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MotionConfig reducedMotion="user">
          <LocaleRoot>
            <ToastProvider>
              <App />
            </ToastProvider>
          </LocaleRoot>
        </MotionConfig>
      </QueryClientProvider>
    </StrictMode>,
  );
});
